import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Snackbar, Stack, Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  ManageAccounts as ManageAccountsIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { adminEndpoints } from '../../config/api';
import { useAuth } from '../../context/AuthContext';

const ROLES = ['user', 'admin'];

const statusColor = (status) => {
  if (status === 'CONFIRMED') return 'success';
  if (status === 'FORCE_CHANGE_PASSWORD') return 'warning';
  if (status === 'UNCONFIRMED') return 'default';
  return 'default';
};

export default function RolesPermissionsPage() {
  const { getAccessToken } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Role dialog
  const [roleDialog, setRoleDialog] = useState({ open: false, user: null, selectedRole: '' });
  const [roleLoading, setRoleLoading] = useState(false);

  // Create user dialog
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', username: '', password: '' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(adminEndpoints.users, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // --- Delete user ---
  const handleDeleteConfirm = async () => {
    if (!deleteDialog.user) return;
    setDeleteLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${adminEndpoints.users}?user-id=${encodeURIComponent(deleteDialog.user.userId)}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to delete user');
      setUsers((prev) => prev.filter((u) => u.userId !== deleteDialog.user.userId));
      showSnackbar('User deleted successfully');
      setDeleteDialog({ open: false, user: null });
    } catch (err) {
      showSnackbar(err.message || 'Failed to delete user', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // --- Change role ---
  const handleRoleSave = async () => {
    if (!roleDialog.user) return;
    setRoleLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(adminEndpoints.users, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: roleDialog.user.userId,
          role: roleDialog.selectedRole,
        }),
      });
      if (!response.ok) throw new Error('Failed to update role');
      setUsers((prev) =>
        prev.map((u) =>
          u.userId === roleDialog.user.userId ? { ...u, role: roleDialog.selectedRole } : u
        )
      );
      showSnackbar(`Role updated to "${roleDialog.selectedRole}"`);
      setRoleDialog({ open: false, user: null, selectedRole: '' });
    } catch (err) {
      showSnackbar(err.message || 'Failed to update role', 'error');
    } finally {
      setRoleLoading(false);
    }
  };

  // --- Create user ---
  const handleCreateUser = async () => {
    setCreateError('');
    if (!createForm.email) {
      setCreateError('Email is required');
      return;
    }
    setCreateLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(adminEndpoints.users, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createForm),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to create user');
      }
      showSnackbar('User created successfully');
      setCreateDialog(false);
      setCreateForm({ email: '', username: '', password: '' });
      fetchUsers();
    } catch (err) {
      setCreateError(err.message || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  // --- Filtering & pagination ---
  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      (u.email || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.userId || '').toLowerCase().includes(q)
    );
  });

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box maxWidth={1100} mx="auto" mt={4} px={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Roles &amp; Permissions</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchUsers} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => { setCreateDialog(true); setCreateError(''); }}
          >
            Create User
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <TextField
          size="small"
          placeholder="Search by email, username or ID…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, width: 340 }}
        />

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                    <TableCell>Email</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>API Key</TableCell>
                    <TableCell>Usage Plan</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedUsers.map((u) => (
                      <TableRow key={u.userId} hover>
                        <TableCell>{u.email || '—'}</TableCell>
                        <TableCell>{u.username}</TableCell>
                        <TableCell>
                          <Chip
                            label={u.status}
                            color={statusColor(u.status)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.enabled ? 'Enabled' : 'Disabled'}
                            color={u.enabled ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.role || 'user'}
                            color={u.role === 'admin' ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {u.apiKeyId ? (
                            <Tooltip title={u.apiKeyId}>
                              <span>{u.apiKeyId.slice(0, 10)}…</span>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {u.usagePlanId || '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Change Role">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setRoleDialog({ open: true, user: u, selectedRole: u.role || 'user' })
                                }
                              >
                                <ManageAccountsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete User">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, user: u })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteDialog.user?.email || deleteDialog.user?.username}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleDialog.open} onClose={() => setRoleDialog({ open: false, user: null, selectedRole: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Change Role</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            User: <strong>{roleDialog.user?.email || roleDialog.user?.username}</strong>
          </Typography>
          <Divider sx={{ my: 1 }} />
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleDialog.selectedRole}
              label="Role"
              onChange={(e) => setRoleDialog((prev) => ({ ...prev, selectedRole: e.target.value }))}
            >
              {ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ open: false, user: null, selectedRole: '' })} disabled={roleLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRoleSave} disabled={roleLoading}>
            {roleLoading ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Create User
          <IconButton size="small" sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setCreateDialog(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {createError && <Alert severity="error" sx={{ mb: 1 }}>{createError}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email *"
              size="small"
              fullWidth
              value={createForm.email}
              onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
              type="email"
            />
            <TextField
              label="Username (optional)"
              size="small"
              fullWidth
              value={createForm.username}
              onChange={(e) => setCreateForm((f) => ({ ...f, username: e.target.value }))}
            />
            <TextField
              label="Temporary Password"
              size="small"
              fullWidth
              value={createForm.password}
              onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
              type="password"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} disabled={createLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateUser} disabled={createLoading}>
            {createLoading ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
